import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import Layout from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Zap, CheckCircle2, XCircle, Loader2, AlertTriangle, ExternalLink, RefreshCw, CreditCard, Webhook, ShieldAlert, Clock } from "lucide-react";

// Session Z64 — Stripe live-key smoke test wizard.
// Guided £1 round-trip so the owner can prove the live keys the moment they
// claim the sandbox. Shows env fingerprint → mints a session → polls for
// webhook arrival → confirms the full round-trip.

function StepPill({ num, label, state }) {
  const styles = {
    pending: "border-slate-200 text-slate-400",
    active: "border-slate-900 text-slate-900 bg-white",
    done: "border-teal-600 bg-teal-600 text-white",
    error: "border-red-600 bg-red-50 text-red-700",
  };
  return (
    <div className="flex items-center gap-2">
      <div className={`w-7 h-7 rounded-full border flex items-center justify-center text-xs font-semibold ${styles[state] || styles.pending}`}>
        {state === "done" ? <CheckCircle2 className="w-4 h-4" /> : state === "error" ? <XCircle className="w-4 h-4" /> : num}
      </div>
      <span className={`text-xs uppercase tracking-wider ${state === "pending" ? "text-slate-400" : "text-slate-700"}`}>
        {label}
      </span>
    </div>
  );
}

function KeyRow({ label, value, tone = "default" }) {
  const toneCls = tone === "warn" ? "text-amber-700" : tone === "ok" ? "text-teal-700" : "text-slate-900";
  return (
    <div className="flex items-center justify-between py-1.5 text-sm">
      <span className="text-slate-500">{label}</span>
      <span className={`font-mono text-xs ${toneCls}`}>{value}</span>
    </div>
  );
}

export default function StripeSmokeTest() {
  const [search] = useSearchParams();
  const [env, setEnv] = useState(null);
  const [envError, setEnvError] = useState(null);
  const [stage, setStage] = useState("idle"); // idle | creating | awaiting_checkout | polling | done | failed
  const [session, setSession] = useState(null);
  const [status, setStatus] = useState(null);
  const [history, setHistory] = useState(null);
  const pollRef = useRef(null);

  const loadHistory = () => {
    api.get("/stripe/smoke-test/history")
      .then((r) => setHistory(r.data))
      .catch(() => setHistory(null));
  };

  // On mount: load env + if we were redirected back from Stripe, pick up the session.
  useEffect(() => {
    api.get("/stripe/smoke-test/env")
      .then((r) => setEnv(r.data))
      .catch((err) => setEnvError(apiError(err, "Couldn't read Stripe env")));
    loadHistory();
    const paidSession = search.get("session_id");
    if (paidSession) {
      setSession({ id: paidSession });
      setStage("polling");
    } else if (search.get("canceled")) {
      toast.info("Smoke test cancelled. Hit the button again when you're ready.");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Poll status while in polling stage.
  useEffect(() => {
    if (stage !== "polling" || !session?.id) return;
    const tick = async () => {
      try {
        const { data } = await api.get(`/stripe/smoke-test/status/${session.id}`);
        setStatus(data);
        if (data.payment_status === "paid") {
          setStage("done");
          toast.success("£1 round-trip complete — your Stripe keys are live.");
          loadHistory();
        } else if (data.payment_status === "failed" || data.status === "failed") {
          setStage("failed");
          toast.error("Payment failed. Check Stripe Dashboard for the exact reason.");
          loadHistory();
        }
      } catch (_err) {
        // transient — keep polling
      }
    };
    tick();
    pollRef.current = setInterval(tick, 2000);
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [stage, session?.id]);

  const launch = async () => {
    setStage("creating");
    try {
      const { data } = await api.post("/stripe/smoke-test", {
        origin_url: window.location.origin,
      });
      setSession(data);
      // Redirect straight to Stripe-hosted checkout.
      setStage("awaiting_checkout");
      window.location.href = data.checkout_url;
    } catch (err) {
      setStage("failed");
      toast.error(apiError(err, "Couldn't launch Stripe checkout"));
    }
  };

  const reset = () => {
    setStage("idle");
    setSession(null);
    setStatus(null);
    if (pollRef.current) clearInterval(pollRef.current);
    window.history.replaceState({}, "", window.location.pathname);
  };

  const stepState = (step) => {
    const order = ["env", "launch", "pay", "webhook"];
    const stageMap = {
      idle: 0, creating: 1, awaiting_checkout: 2, polling: 3, done: 4, failed: 4,
    };
    const now = stageMap[stage] || 0;
    const idx = order.indexOf(step);
    if (idx < now) return stage === "failed" && step === "webhook" ? "error" : "done";
    if (idx === now) return stage === "failed" ? "error" : "active";
    return "pending";
  };

  return (
    <Layout
      title="Stripe live-key smoke test"
      subtitle="A guided £1 round-trip so you can prove your Stripe keys the moment you claim the sandbox. Refund the £1 from the Stripe Dashboard once the test lights up green."
    >
      <section className="ns-card p-6 max-w-3xl" data-testid="stripe-smoke-test">
        <div className="flex items-start justify-between gap-4 mb-5 flex-wrap">
          <div className="min-w-0">
            <div className="ns-label">Live-key smoke test</div>
            <div className="font-serif text-xl mt-1">£1 round-trip wizard</div>
            <div className="text-xs text-slate-400 mt-1">
              Creates a real Stripe Checkout Session, redirects you to Stripe-hosted checkout, and waits for the webhook to come home.
            </div>
          </div>
          <div className="w-10 h-10 rounded-full bg-amber-50 text-amber-700 flex items-center justify-center shrink-0">
            <Zap className="w-5 h-5" />
          </div>
        </div>

        <div className="flex items-center gap-4 mb-6 flex-wrap">
          <StepPill num={1} label="Env check" state={stepState("env")} />
          <span className="h-px flex-1 bg-slate-200 min-w-4" />
          <StepPill num={2} label="Launch £1 checkout" state={stepState("launch")} />
          <span className="h-px flex-1 bg-slate-200 min-w-4" />
          <StepPill num={3} label="Pay on Stripe" state={stepState("pay")} />
          <span className="h-px flex-1 bg-slate-200 min-w-4" />
          <StepPill num={4} label="Webhook arrival" state={stepState("webhook")} />
        </div>

        {/* Env panel */}
        <div className="rounded-xl border border-slate-100 bg-stone-50/60 p-5 mb-5" data-testid="stripe-smoke-env">
          <div className="ns-label text-teal-700 mb-2 flex items-center gap-2">
            <ShieldAlert className="w-3 h-3" /> Backend wiring
          </div>
          {envError && (
            <div className="text-sm text-red-700">{envError}</div>
          )}
          {!env && !envError && (
            <div className="text-sm text-slate-400 italic">Reading backend env…</div>
          )}
          {env && (
            <>
              <KeyRow
                label="Mode"
                value={env.mode.toUpperCase()}
                tone={env.mode === "live" ? "ok" : env.is_shared_sandbox ? "warn" : "default"}
              />
              <KeyRow label="Stripe account" value={env.stripe_account_id || "(not set)"} />
              <KeyRow label="Secret key" value={env.secret_key_fingerprint} />
              <KeyRow label="Publishable key" value={env.publishable_key_fingerprint || "(not set)"} />
              <KeyRow
                label="Webhook secret"
                value={env.webhook_secret_set ? "configured" : "MISSING"}
                tone={env.webhook_secret_set ? "ok" : "warn"}
              />
              {env.is_shared_sandbox && (
                <div className="mt-3 text-[11px] text-amber-700 flex items-start gap-2">
                  <AlertTriangle className="w-3 h-3 mt-0.5 shrink-0" />
                  Currently using the shared `sk_test_emergent` fallback — claim your sandbox from <b>Manage → Payments</b> to get your own test keys.
                </div>
              )}
              {env.mode === "live" && (
                <div className="mt-3 text-[11px] text-red-700 flex items-start gap-2">
                  <AlertTriangle className="w-3 h-3 mt-0.5 shrink-0" />
                  Live mode detected — this £1 will leave a real card. Refund immediately from the Stripe Dashboard.
                </div>
              )}
            </>
          )}
        </div>

        {/* Action buttons */}
        {stage === "idle" && (
          <div className="flex items-center gap-3">
            <Button
              type="button"
              data-testid="stripe-smoke-launch"
              onClick={launch}
              disabled={!env}
              className="rounded-full ns-btn-primary h-11 px-6 gap-2"
            >
              <CreditCard className="w-4 h-4" />
              Launch £1 checkout
            </Button>
            <span className="text-[11px] text-slate-400">
              You'll be redirected to Stripe. Return here to see the webhook land.
            </span>
          </div>
        )}

        {stage === "creating" && (
          <div className="flex items-center gap-2 text-slate-600 text-sm">
            <Loader2 className="w-4 h-4 animate-spin" />
            Minting Stripe Checkout Session…
          </div>
        )}

        {(stage === "polling" || stage === "done" || stage === "failed") && session && (
          <div className="rounded-xl border border-slate-100 bg-white p-5" data-testid="stripe-smoke-result">
            <div className="flex items-center gap-2 ns-label text-teal-700 mb-3">
              <Webhook className="w-3 h-3" />
              Live status
            </div>
            <KeyRow label="Session ID" value={session.id} />
            {status && (
              <>
                <KeyRow label="Status" value={status.status || "pending"} tone={status.status === "completed" ? "ok" : "default"} />
                <KeyRow label="Payment" value={status.payment_status || "pending"} tone={status.payment_status === "paid" ? "ok" : status.payment_status === "failed" ? "warn" : "default"} />
                <KeyRow label="Payment Intent" value={status.payment_intent_id || "—"} />
                {status.payment_status === "paid" && (
                  <KeyRow
                    label="Resolved via"
                    value={status.resolved_via_fallback ? "Stripe fallback poll" : "Webhook delivery"}
                    tone={status.resolved_via_fallback ? "warn" : "ok"}
                  />
                )}
              </>
            )}
            {stage === "polling" && (
              <div className="mt-4 flex items-center gap-2 text-sm text-slate-500">
                <Loader2 className="w-4 h-4 animate-spin" />
                Waiting for the webhook… (polling every 2s)
              </div>
            )}
            {stage === "done" && (
              <>
                <div className="mt-4 rounded-lg bg-teal-50 border border-teal-100 text-teal-800 text-sm p-4">
                  <div className="font-semibold">✓ Live keys confirmed.</div>
                  <div className="text-[12px] mt-1">
                    The £1 payment was captured and the webhook came home. Refund it now from
                    {" "}<a href="https://dashboard.stripe.com/payments" target="_blank" rel="noreferrer" className="underline inline-flex items-center gap-1">Stripe Dashboard <ExternalLink className="w-3 h-3" /></a>{" "}
                    so you don't carry the charge.
                  </div>
                  {status?.resolved_via_fallback && (
                    <div className="text-[11px] mt-2 text-amber-700">
                      Note: resolved by Stripe-side poll, not the webhook — double-check the webhook endpoint in Stripe Dashboard is pointing at <code>/api/stripe/webhook</code>.
                    </div>
                  )}
                </div>
                <div className="mt-3">
                  <Button
                    type="button"
                    variant="outline"
                    data-testid="stripe-smoke-reset"
                    onClick={reset}
                    className="rounded-full h-10 px-4 gap-2"
                  >
                    <RefreshCw className="w-4 h-4" />
                    Run again
                  </Button>
                </div>
              </>
            )}
            {stage === "failed" && (
              <div className="mt-4 rounded-lg bg-red-50 border border-red-100 text-red-800 text-sm p-4">
                <div className="font-semibold">Checkout did not complete.</div>
                <div className="text-[12px] mt-1">
                  Look up the session in the Stripe Dashboard for the exact decline reason, then
                  <button onClick={reset} className="underline ml-1">run again</button>.
                </div>
              </div>
            )}
          </div>
        )}

        {/* Session Z65 — recent runs history. Catches webhook config drift
            over time, not just on the first green test. */}
        {history && history.items.length > 0 && (
          <div className="mt-6 rounded-xl border border-slate-100 bg-white p-5" data-testid="stripe-smoke-history">
            <div className="flex items-center justify-between gap-4 mb-3">
              <div className="flex items-center gap-2">
                <div className="ns-label text-teal-700 flex items-center gap-2">
                  <Clock className="w-3 h-3" /> Recent runs
                </div>
              </div>
              <div className="flex items-center gap-2 text-[11px]">
                {history.webhook_count > 0 && (
                  <span className="inline-flex items-center gap-1 bg-teal-50 text-teal-700 px-2 py-0.5 rounded-full">
                    {history.webhook_count} via webhook
                  </span>
                )}
                {history.fallback_count > 0 && (
                  <span
                    data-testid="stripe-smoke-history-fallback-warning"
                    className="inline-flex items-center gap-1 bg-amber-50 text-amber-700 px-2 py-0.5 rounded-full"
                    title="Resolved via Stripe-side poll, not the webhook. Suggests a webhook config drift."
                  >
                    <AlertTriangle className="w-3 h-3" />
                    {history.fallback_count} via fallback
                  </span>
                )}
                {history.pending_count > 0 && (
                  <span className="inline-flex items-center gap-1 bg-slate-100 text-slate-600 px-2 py-0.5 rounded-full">
                    {history.pending_count} pending
                  </span>
                )}
              </div>
            </div>
            {history.fallback_count >= 2 && (
              <div className="text-[11px] text-amber-700 bg-amber-50/40 border border-amber-100 rounded p-2 mb-3 flex items-start gap-2">
                <AlertTriangle className="w-3 h-3 mt-0.5 shrink-0" />
                Two or more recent runs resolved via Stripe fallback instead of the webhook. Verify the webhook endpoint in Stripe Dashboard points at <code>/api/stripe/webhook</code> and is subscribed to <code>checkout.session.completed</code>.
              </div>
            )}
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-slate-500 text-[11px] uppercase tracking-wider">
                  <th className="py-2 pr-4">When</th>
                  <th className="py-2 pr-4">Resolved via</th>
                  <th className="py-2 pr-4">Payment</th>
                  <th className="py-2 pr-4">By</th>
                  <th className="py-2 pr-4">Session</th>
                </tr>
              </thead>
              <tbody>
                {history.items.map((h) => (
                  <tr key={h.session_id} className="border-t border-slate-100">
                    <td className="py-2 pr-4 text-slate-600 text-[12px]">
                      {(() => { try { return new Date(h.created_at).toLocaleString("en-GB", { dateStyle: "medium", timeStyle: "short" }); } catch (_e) { return h.created_at; } })()}
                    </td>
                    <td className="py-2 pr-4">
                      {h.resolved_via === "webhook" && <span className="text-teal-700 text-[11px]">✓ Webhook</span>}
                      {h.resolved_via === "fallback" && <span className="text-amber-700 text-[11px]">⚠ Fallback</span>}
                      {h.resolved_via === "pending" && <span className="text-slate-400 text-[11px]">… Pending</span>}
                    </td>
                    <td className="py-2 pr-4 text-[12px] text-slate-600">{h.payment_status || "—"}</td>
                    <td className="py-2 pr-4 text-[12px] text-slate-500">{h.initiated_by || "—"}</td>
                    <td className="py-2 pr-4 text-[10px] font-mono text-slate-400">{h.session_id.slice(0, 18)}…</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </Layout>
  );
}
